import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from "@/components/ui/select";
import { MESSAGE } from "@/constants/message";
import { ScoringOptions, ScoringOptionsEnum } from "@/types/spatial";

interface ScoringOptionSidebarProps {
  selectedScoringOption: ScoringOptions | null;
  setSelectedScoringOption: (scoringOption: ScoringOptions | null) => void;
  /**
   * When true the method is locked: the dropdown is replaced by a static
   * label. Used by the advanced panel, which is only reachable after the user
   * already picked "Normalization" in the simple panel — the method follows
   * that choice and must not be re-selected here.
   */
  readOnly?: boolean;
}

const SCORING_OPTIONS: {
  value: ScoringOptions;
  label: string;
  description: string;
}[] = [
  {
    value: ScoringOptionsEnum.NORMALIZED,
    label: "Normalization",
    description:
      "Normalization aims to modify the existing grid scoring calculations."
  },
  {
    value: ScoringOptionsEnum.SCALED,
    label: "Scaled Value",
    description:
      "Score are calculated after applying a log 10 transformation to reduce extreme data variation"
  },
  {
    value: ScoringOptionsEnum.RAW,
    label: "Raw Value",
    description: "Calculate scores directly from original data."
  }
];

export default function ScoringOptionSidebar({
  selectedScoringOption,
  setSelectedScoringOption,
  readOnly = false
}: Readonly<ScoringOptionSidebarProps>) {
  const currentOption =
    SCORING_OPTIONS.find((option) => option.value === selectedScoringOption) ??
    SCORING_OPTIONS[0];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-txtHeading">
          Scoring Option
        </span>
        {!readOnly && (
          <span className="text-xs text-txtBody">
            Select how scores are calculated and which formula to apply.
          </span>
        )}
        {readOnly ? (
          <div
            className="mt-3 flex flex-col gap-0.5"
            aria-label="Scoring Option"
            data-testid="scoring-option-locked"
          >
            <span className="font-semibold text-gray text-xs">
              {currentOption.label}
            </span>
            <span className="text-txtBody text-[10px]">
              {currentOption.description}
            </span>
          </div>
        ) : (
          <Select
            value={currentOption.value}
            onValueChange={(value) =>
              setSelectedScoringOption(value as ScoringOptions)
            }
          >
            <SelectTrigger
              className="h-10 mt-1 text-sm"
              aria-label="Scoring Option"
              data-testid="scoring-option-select"
            >
              <span className="text-txtHeading">{currentOption.label}</span>
            </SelectTrigger>
            <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]">
              {SCORING_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="items-start py-2 pl-3 pr-3 focus:bg-[#E6F4F2] data-[highlighted]:bg-[#E6F4F2] data-[state=checked]:bg-[#E6F4F2]"
                >
                  <span className="block whitespace-normal text-sm font-semibold text-txtHeading">
                    {option.label}
                  </span>
                  <span className="block whitespace-normal text-xs text-txtBody mt-0.5">
                    {option.description}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {currentOption.value === ScoringOptionsEnum.NORMALIZED && (
        <div className="rounded-lg border border-neutral-200 bg-[#FAFAFA] p-3">
          <p className="text-sm font-semibold text-txtBody mb-0.5">
            {MESSAGE.SCORING_OPTION.NEGATIVE_IMPACT_TITLE}
          </p>
          <p className="text-xs text-txtBody">
            {MESSAGE.SCORING_OPTION.NEGATIVE_IMPACT_DESCRIPTION}
          </p>
        </div>
      )}
    </div>
  );
}
